import { useId } from 'react';
import { contactTypeView, labelOf, type ContactTypeOptionsLike } from './contactType.js';

/**
 * Contact Type: what kind of contact this is in the CRM. One control for every Review
 * screen — email, business card, import — so it reads and behaves the same everywhere.
 *
 * The choices are the CRM's own, read from the connected account. Nothing is pre-chosen:
 * not from a tag, not from where the contact came from, not by a model. An existing
 * contact's type belongs to the CRM and is shown as it is there.
 */
export function ContactTypeField({
  types, status, value, existingValue, onChange, disabled, idBase, keap, proposed, onPropose, onRetryRead,
}: {
  /**
   * An existing contact's type as Keap holds it now. With `onPropose`, the field is editable:
   * choosing another value PROPOSES a change ("Will update Keap: Other → Client"), which only
   * an approval sends. Without them, the type is shown read-only, as before.
   */
  keap?: { status: 'ok' | 'unavailable'; contactType: string | null };
  proposed?: string | null;
  onPropose?: (to: string | null) => void;
  onRetryRead?: () => void;
  types: ContactTypeOptionsLike | null | undefined;
  status: 'new' | 'existing' | 'other';
  value: string | null | undefined;
  existingValue?: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  /** Distinguishes several fields on one screen. */
  idBase?: string;
}) {
  const auto = useId();
  const id = `${idBase ?? auto}-contact-type`;
  const view = contactTypeView({ types, status, value, existingValue: existingValue ?? null });
  if (view.kind === 'hidden' || !types) return null;

  if (view.kind === 'existing' && keap && onPropose) {
    const current = keap.contactType;
    const label = (v: string | null) => (v ? labelOf(types, v) : 'Not set');
    if (keap.status === 'unavailable') {
      return (
        <div className="min-w-0 text-sm" data-contact-type="existing-unavailable" role="status">
          <p className="font-medium text-slate-700">Contact Type</p>
          <p className="text-xs text-amber-900">Unable to load Contact Type from Keap.</p>
          {onRetryRead ? (
            <button type="button" onClick={onRetryRead}
              className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
              Try again
            </button>
          ) : null}
        </div>
      );
    }
    // A value Keap holds that is not one of its Contact Types is shown as it is, and not changed from here.
    const unsupported = current !== null && !types.options.some((o) => o.id === current);
    const shown = proposed ?? current ?? '';
    return (
      <div className="min-w-0" data-contact-type="existing">
        <label className="block text-sm text-slate-700" htmlFor={id}>Contact Type</label>
        {unsupported || !types.available ? (
          <p className="mt-1 text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">
            {current ?? 'Not set in Keap'}
            {unsupported ? <span className="block text-xs font-normal text-slate-500">Not one of your Keap Contact Types, so it can’t be changed here.</span> : null}
          </p>
        ) : (
          <select
            id={id} value={shown} disabled={disabled}
            onChange={(e) => { const v = e.target.value || null; onPropose(v === current ? null : v); }}
            className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm disabled:bg-slate-50"
          >
            {current === null ? <option value="">Not set in Keap</option> : null}
            {types.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        )}
        <p className="text-xs text-slate-600" data-contact-type-change={proposed && proposed !== current ? 'pending' : 'none'}>
          {proposed && proposed !== current
            ? <>Will update Keap: <span className="font-medium">{label(current)} → {label(proposed)}</span> when you approve.</>
            : current ? `Currently ${label(current)} in Keap.` : 'Not set in Keap.'}
        </p>
      </div>
    );
  }

  if (view.kind === 'existing') {
    return (
      <div className="min-w-0 text-sm text-slate-700" data-contact-type="existing">
        <span className="text-slate-500">Contact Type: </span>
        {/*
          * No value is "untyped in the CRM" or "not read here" (someone a message of this
          * conversation already sent). Both are true of one sentence; "Not set" was not.
          */}
        <span className="font-medium text-slate-900 [overflow-wrap:anywhere]">{view.label ?? 'kept as it is in your CRM'}</span>
        {view.label ? <p className="text-xs text-slate-500">As recorded in your CRM. ContactPilot keeps it as it is.</p> : null}
      </div>
    );
  }

  if (view.kind === 'unavailable') {
    return (
      <div className="min-w-0 text-sm" data-contact-type="unavailable" role="status">
        <p className="font-medium text-slate-700">Contact Type</p>
        <p className="text-xs text-amber-900">Contact types could not be loaded from your CRM. Try again in a moment.</p>
      </div>
    );
  }

  const hintId = `${id}-hint`;
  return (
    <div className="min-w-0" data-contact-type="choose">
      <label htmlFor={id} className="block text-sm text-slate-700">
        Contact Type{types.required ? <span className="sr-only"> (required)</span> : null}
      </label>
      <select
        id={id}
        value={value && !view.unknownValue ? value : ''}
        disabled={disabled}
        required={types.required}
        aria-invalid={view.missing || undefined}
        aria-describedby={hintId}
        onChange={(e) => onChange(e.target.value || null)}
        className="mt-1 min-h-11 w-full min-w-0 max-w-full truncate rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm disabled:bg-slate-50 disabled:text-slate-500"
      >
        <option value="">{types.required ? 'Choose a contact type' : 'Not set'}</option>
        {types.options.map((o) => <option key={o.id} value={o.id}>{labelOf(types, o.id)}</option>)}
      </select>
      <p id={hintId} className={`mt-1 text-xs [overflow-wrap:anywhere] ${view.missing ? 'text-amber-900' : 'text-slate-500'}`}>
        {view.unknownValue
          ? `"${view.unknownValue}" is not a contact type in your CRM. Choose one from the list.`
          : view.missing
            ? 'Required for a new contact. Your CRM would otherwise apply its own default.'
            : 'What kind of contact this is in your CRM. Separate from CRM Tags.'}
      </p>
    </div>
  );
}
